import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataCard } from 'v5/patterns/collection/DataCard';
import type { TileState } from 'v5/patterns/collection/TileCollection';
import { TileShell } from 'v5/patterns/collection/TileShell';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import { cn } from 'v5/platform/dom/cn';
import { EntityListRowCellErrorBoundary } from '../EntityListRowCellErrorBoundary';
import {
  asEntityForUnsafeRender,
  ValidatedRowInvalidRenderPanel
} from '../ValidatedRowInvalidRenderPanel';
import { formatZodErrorIssues } from '../rowValidationDisplay';
import { EntityListRowValidationIndicator } from '../EntityListRowValidationIndicator';
import { EntityGridTileStatusProvider } from './EntityGridTileStatus';

/**
 * Invokes `render()` during this component’s render so throws are handled by the enclosing
 * {@link EntityListRowCellErrorBoundary} (same idea as `DeferredTableCellRender` in the table).
 */
export function EntityGridDeferredRowRender({
  render
}: {
  render: () => ReactNode;
}) {
  return <>{render()}</>;
}

EntityGridDeferredRowRender.displayName = 'EntityGridDeferredRowRender';

export interface EntityGridListItemProps<
  TEntity extends { id: string | number }
> {
  row: ValidatedRow<TEntity>;
  /** The collection's tile: the shared chrome reads it. */
  tile: TileState;
  /** Tile body for valid rows; also used inside the invalid-row “display anyway” panel. */
  renderItem: (entity: TEntity) => ReactNode;
  advancedValidationRowUi?: boolean;
}

/** One row of the entity grid on the shared tile: its body, or what stands in for a row that failed its checks. */
export function EntityGridListItem<TEntity extends { id: string | number }>({
  row,
  tile,
  renderItem,
  advancedValidationRowUi = false
}: EntityGridListItemProps<TEntity>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'EntityGridListItem' });
  const { t: tRow } = useTranslation('patterns', {
    keyPrefix: 'EntityListRow'
  });
  const renderCrashLabels = {
    title: tRow('renderCrashed'),
    tryAgain: tRow('renderTryAgain')
  };
  const [gridUnsafePreview, setGridUnsafePreview] = useState(false);
  const labels = {
    selectLabel: t('selectItem', { key: String(row.id) }),
    deselectLabel: t('deselectItem', { key: String(row.id) })
  };
  const cellFallbackInset = tile.selectable
    ? 'mx-2 mb-2 mt-11 shrink-0'
    : 'm-2 shrink-0';

  if (!row.isValid) {
    if (!advancedValidationRowUi && row.rawItem !== undefined) {
      const unsafeEntity = asEntityForUnsafeRender<TEntity>(row.rawItem);
      return (
        <TileShell
          tile={tile}
          {...labels}
          attention
          data-item-id={String(row.id)}
          data-row-id={String(row.id)}
        >
          <EntityListRowCellErrorBoundary
            labels={renderCrashLabels}
            density="default"
            fallbackClassName={cellFallbackInset}
          >
            <EntityGridTileStatusProvider
              status={
                <EntityListRowValidationIndicator
                  error={row.error}
                  rawItem={row.rawItem}
                />
              }
            >
              <div className="min-h-0 flex-1 flex flex-col">
                <EntityGridDeferredRowRender
                  render={() => renderItem(unsafeEntity)}
                />
              </div>
            </EntityGridTileStatusProvider>
          </EntityListRowCellErrorBoundary>
        </TileShell>
      );
    }

    return (
      // the panel is the tile's content: a press doesn't open the row
      <TileShell
        tile={{ ...tile, onAction: undefined }}
        {...labels}
        data-item-id={String(row.id)}
      >
        <EntityListRowCellErrorBoundary
          labels={renderCrashLabels}
          density="default"
          fallbackClassName={cellFallbackInset}
        >
          <DataCard
            className={cn(
              'min-h-0 flex-1 flex flex-col',
              !gridUnsafePreview && 'border-border-danger/35'
            )}
          >
            <ValidatedRowInvalidRenderPanel<TEntity>
              error={row.error}
              rawItem={row.rawItem}
              renderItem={renderItem}
              displayAnywayDataRowId={String(row.id)}
              unsafePreview={gridUnsafePreview}
              onUnsafePreviewChange={setGridUnsafePreview}
              invalidGridTileChrome
            />
          </DataCard>
        </EntityListRowCellErrorBoundary>
      </TileShell>
    );
  }

  const entity = row.data;
  return (
    <TileShell tile={tile} {...labels} data-item-id={String(row.id)}>
      <EntityListRowCellErrorBoundary
        labels={renderCrashLabels}
        density="default"
        fallbackClassName={cellFallbackInset}
      >
        <EntityGridDeferredRowRender render={() => renderItem(entity)} />
      </EntityListRowCellErrorBoundary>
    </TileShell>
  );
}
EntityGridListItem.displayName = 'EntityGridListItem';

/** The text a row's tile reads as: its id, or that it failed its checks and why. */
export function entityGridRowText<TEntity extends { id: string | number }>(
  row: ValidatedRow<TEntity>,
  validationFailed: string
): string {
  return row.isValid
    ? String(row.id)
    : `${validationFailed}: ${formatZodErrorIssues(row.error)}`;
}
